import React, { useState, useContext } from 'react';
import {
  AppBar,
  CircularProgress,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import GroupIcon from '@mui/icons-material/Group';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BarChartIcon from '@mui/icons-material/BarChart';
import BlockIcon from '@mui/icons-material/Block';
import { UserContext } from '../hooks';
import { useNavigate } from 'react-router-dom';
import Header from './Header';

export const BaseLayout = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null); // For profile menu
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const handleNavigate = path => {
    setDrawerOpen(false); // Cierra el Drawer inmediatamente
    if (path) setTimeout(() => navigate(path), 300);
  };

  const handleProfileMenuOpen = event => {
    setMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleLogOut = () => {
    setMenuAnchor(null);
    setUser(null);
    localStorage.clear();
    handleNavigate('/');
  };

  if (!user) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isSocio = user.role === 'user';

  return (
    <Box>
      {/* Header */}
      <Header />

      {/* AppBar with Sidebar Menu */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* User Avatar and Welcome Message */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              gap: 2,
            }}
          >
            <Avatar
              src={user.image}
              alt="Profile"
              sx={{
                width: 40,
                height: 40,
                border: isSocio ? '2px solid #4caf50' : '2px solid #ccc',
                cursor: 'pointer',
              }}
              onClick={handleProfileMenuOpen}
            />
            <Typography
              variant="h6"
              color="inherit"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              Bienvenido, {`${user.name || ''} ${user.surname || ''}`.trim()}{' '}
              {isSocio && (
                <Tooltip title="Socio Activo: Beneficios exclusivos en reservas">
                  <WorkspacePremiumIcon
                    sx={{
                      color: '#ffc107',
                      fontSize: 24,
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'flex-end',
            }}
          >
            <Button color="inherit" onClick={() => handleNavigate('/home')}>
              Inicio
            </Button>
            <Button color="inherit" onClick={() => handleNavigate('/bookings')}>
              Mis reservas
            </Button>
            <Button
              color="inherit"
              onClick={() => handleNavigate('/tennis-school')}
            >
              Escuela de Tenis
            </Button>
            {/*<Button color="inherit" onClick={() => handleNavigate('/shop')}>*/}
            {/*  Tienda*/}
            {/*</Button>*/}
            <Button color="inherit" onClick={() => handleNavigate('/profile')}>
              Mi perfil
            </Button>
            {user.role === 'admin' && (
              <Button color="inherit" onClick={() => handleNavigate('/admin')}>
                Administración
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleNavigate('/profile')}>
          <PersonIcon sx={{ marginRight: 1 }} />
          Mi Perfil
        </MenuItem>
        <MenuItem onClick={handleLogOut}>
          <LogoutIcon sx={{ marginRight: 1 }} />
          Cerrar sesión
        </MenuItem>
      </Menu>

      {/* Drawer Menu */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box
          sx={{
            width: 250,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
          role="presentation"
        >
          <List>
            <ListItemButton onClick={() => handleNavigate('/home')}>
              <ListItemIcon>
                <HomeIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Inicio" />
            </ListItemButton>

            <ListItemButton onClick={() => handleNavigate('/bookings')}>
              <ListItemIcon>
                <EventNoteIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Mis reservas" />
            </ListItemButton>

            <ListItemButton onClick={() => handleNavigate('/tennis-school')}>
              <ListItemIcon>
                <SportsTennisIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Escuela de Tenis" />
            </ListItemButton>

            {/*<ListItemButton onClick={() => handleNavigate('/shop')}>*/}
            {/*  <ListItemIcon>*/}
            {/*    <StoreIcon color="primary" />*/}
            {/*  </ListItemIcon>*/}
            {/*  <ListItemText primary="Tienda" />*/}
            {/*</ListItemButton>*/}

            <ListItemButton onClick={() => handleNavigate('/profile')}>
              <ListItemIcon>
                <PersonIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Perfil" />
            </ListItemButton>

            {user.role === 'admin' && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListSubheader component="div">Administración</ListSubheader>
                <ListItemButton onClick={() => handleNavigate('/admin')}>
                  <ListItemIcon>
                    <AdminPanelSettingsIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Panel principal" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigate('/admin/users')}
                >
                  <ListItemIcon>
                    <GroupIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Usuarios" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigate('/admin/reservations')}
                >
                  <ListItemIcon>
                    <CalendarMonthIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Reservas" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigate('/admin/courts')}
                >
                  <ListItemIcon>
                    <SportsTennisIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Pistas" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigate('/admin/access')}
                >
                  <ListItemIcon>
                    <VpnKeyIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Accesos" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigate('/admin/statistics')}
                >
                  <ListItemIcon>
                    <BarChartIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Estadísticas" />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigate('/admin/blocked-times')}
                >
                  <ListItemIcon>
                    <BlockIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Bloqueos" />
                </ListItemButton>
              </>
            )}
          </List>

          <Box>
            <Divider />
            <ListItemButton onClick={handleLogOut}>
              <ListItemIcon>
                <LogoutIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Cerrar sesión" />
            </ListItemButton>
          </Box>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ p: 3 }}>{children}</Box>
    </Box>
  );
};
